import React from 'react';
import { useDropzone, DropzoneOptions, DropzoneRootProps, DropzoneInputProps } from 'react-dropzone';
import { 
  Paper, 
  Typography, 
  Button, 
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { styled } from '@mui/system';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { DocumentGuidance } from '../../contexts/TenantConfigContext';
import { scribePrimaryCtaContainerSx } from '../../constants/scribeUi';

export const StyledDropzonePaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  backgroundColor: 'var(--s1, #f0f2f5)',
  border: '1.5px dashed rgba(26,33,102,0.30)',
  borderRadius: 'var(--radius-lg, 24px)',
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
  '&:hover': {
    borderColor: 'var(--teal, #1a2166)',
    backgroundColor: 'var(--teal-dim, rgba(26,33,102,0.06))',
    boxShadow: '0 0 30px rgba(26,33,102,0.06)',
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 180,
}));

export enum MimeType {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  EML = 'message/rfc822',
  MSG = 'application/vnd.ms-outlook',
  AUDIO_MPEG = 'audio/mpeg',
  AUDIO_MP4 = 'audio/mp4',
  AUDIO_X_M4A = 'audio/x-m4a',
  VIDEO_MP4 = 'video/mp4',
  AUDIO_WAV = 'audio/wav',
  AUDIO_X_WAV = 'audio/x-wav',
  AUDIO_FLAC = 'audio/flac',
  AUDIO_WEBM = 'audio/webm',
  AUDIO_OGG = 'audio/ogg',
  AUDIO_OPUS = 'audio/opus',
}

export enum FileExtension {
  PDF = '.pdf',
  DOCX = '.docx',
  XLSX = '.xlsx',
  EML = '.eml',
  MSG = '.msg',
  MP3 = '.mp3',
  M4A = '.m4a',
  MP4 = '.mp4',
  WAV = '.wav',
  FLAC = '.flac',
  WEBM = '.webm',
  OGG = '.ogg',
  OPUS = '.opus',
}

export const DOCUMENT_ACCEPT: Record<string, string[]> = {
  [MimeType.PDF]: [FileExtension.PDF],
  [MimeType.DOCX]: [FileExtension.DOCX],
  [MimeType.XLSX]: [FileExtension.XLSX],
  [MimeType.EML]: [FileExtension.EML],
  [MimeType.MSG]: [FileExtension.MSG],
};

export const AUDIO_ACCEPT: Record<string, string[]> = {
  [MimeType.AUDIO_MPEG]: [FileExtension.MP3],
  [MimeType.AUDIO_MP4]: [FileExtension.M4A],
  [MimeType.AUDIO_X_M4A]: [FileExtension.M4A],
  [MimeType.VIDEO_MP4]: [FileExtension.MP4],
  [MimeType.AUDIO_WAV]: [FileExtension.WAV],
  [MimeType.AUDIO_X_WAV]: [FileExtension.WAV],
  [MimeType.AUDIO_FLAC]: [FileExtension.FLAC],
  [MimeType.AUDIO_WEBM]: [FileExtension.WEBM],
  [MimeType.AUDIO_OGG]: [FileExtension.OGG],
  [MimeType.AUDIO_OPUS]: [FileExtension.OPUS],
};

type GenericDropzoneProps = Omit<DropzoneOptions, 'accept'> & {
  mainText?: string;
  subText?: string;
  buttonText?: string;
  documentGuidance?: DocumentGuidance;
  acceptOverride?: Record<string, string[]>;
};

const Dropzone: React.FC<GenericDropzoneProps> = ({
  mainText = "Upload Documents",
  subText = "Upload documents for analysis",
  buttonText = "Select Files (.pdf, .docx, .eml, .msg)",
  documentGuidance,
  acceptOverride,
  ...dropzoneProps
}) => {
  const enforcedAccept = acceptOverride ?? DOCUMENT_ACCEPT;

  const { getRootProps, getInputProps, isDragActive, open: openFileDialog }: {
    getRootProps: (props?: DropzoneRootProps | undefined) => DropzoneRootProps;
    getInputProps: (props?: DropzoneInputProps | undefined) => DropzoneInputProps;
    isDragActive: boolean;
    open: () => void;
  } = useDropzone({
    ...dropzoneProps,
    accept: enforcedAccept,
  });

  return (
    // Use a div wrapper for the className, and the Paper for styling via styled-components
    <div>
    <div {...getRootProps()} className={`file-drop-area ${isDragActive ? 'active' : ''}`}>
      <StyledDropzonePaper elevation={0}>
        <input {...getInputProps()} />
        
        <CloudUploadIcon sx={{ fontSize: 60, color: 'hsl(var(--primary))', mb: 2 }} />
        <Typography variant="h6" component="p" sx={{ fontWeight: 500, mb: 1 }}>
          {mainText}
        </Typography>
        <Typography variant="body2" sx={{ color: 'hsl(var(--muted-foreground))', mb: 2 }}>
          {subText}
        </Typography>
        <Box sx={scribePrimaryCtaContainerSx}>
          <Button
            variant="contained"
            fullWidth
            onClick={(e) => {
              e.stopPropagation(); // Prevent dropzone click through
              openFileDialog(); // Open file dialog
            }}
            sx={{
              borderRadius: 999,
              py: 1.25,
              backgroundColor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              '&:hover': {
                backgroundColor: 'hsl(var(--primary) / 0.9)', // Slightly darker on hover
              },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      </StyledDropzonePaper>
    </div>

      {/* Documents to Upload - Collapsible Accordion (outside dropzone) */}
      {documentGuidance && (
        <Accordion 
          sx={{ 
            mt: 2,
            boxShadow: 0,
            '&:before': {
              display: 'none',
            },
            border: '1px solid',
            borderColor: 'divider'
          }}
          defaultExpanded={false}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Documents to upload
              </Typography>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ pt: 2 }}>
            <Box>
              {/* Title & Description */}
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
                {documentGuidance.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {documentGuidance.shortDescription}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Recommended Documents */}
              <List dense>
                {documentGuidance.recommendedDocuments.map((doc, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={doc}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* Examples (Optional) */}
              {documentGuidance.examples && documentGuidance.examples.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    Examples
                  </Typography>
                  <List dense>
                    {documentGuidance.examples.map((example, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemText 
                          primary={`• ${example}`}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

export default Dropzone;
